<script setup lang="ts">
import { ref } from 'vue'
import type { DynamicFormSchema, FormField, FormSection } from '@/types/form'
import { evaluateCondition } from '@/utils/formConditionUtils'
import TextInput from '@/components/common/inputs/TextInput.vue'
import NumberInput from '@/components/common/inputs/NumberInput.vue'
import DateInput from '@/components/common/inputs/DateInput.vue'
import RadioInput from '@/components/common/inputs/RadioInput.vue'
import OrderFileInput from '@/components/common/inputs/OrderFileInput.vue'
import WysiwygInput from '@/components/common/inputs/WysiwygInput.vue'
import SwitchInput from '@/components/common/inputs/SwitchInput.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'

interface OrderFile {
  uuid: string
  fieldName: string
  fileName: string
  path: string
  orderIndex?: number
}

const props = withDefaults(
  defineProps<{
    schema: DynamicFormSchema
    orderFiles?: OrderFile[]
    fieldsDisabled?: boolean
    /** Index du site/item en contexte multisite (pour résoudre {{index}} dans les conditions) */
    repeatableIndex?: number | string
    /** Masquer les titres des sections (évite la duplication quand le parent affiche déjà le titre) */
    embedSections?: boolean
  }>(),
  { orderFiles: () => [], fieldsDisabled: false, embedSections: false },
)

function isFieldDisabled(field: FormField): boolean {
  return !!field.disabled || props.fieldsDisabled
}

const emit = defineEmits<{
  deleteFile: [uuid: string, context?: { sectionId: string; orderIndex: number }]
}>()

function handleDeleteFile(uuid: string, ctx?: { sectionId: string; orderIndex: number }) {
  emit('deleteFile', uuid, ctx)
}

function handleDeleteFileWithContext(sectionId: string, orderIndex: number) {
  return (uuid: string) => emit('deleteFile', uuid, { sectionId, orderIndex })
}

function isFileOnlyRepeatableSection(section: FormSection): boolean {
  return !!(section.repeatable && section.fields?.length === 1 && section.fields[0].type === 'file')
}

const suiteUrl = import.meta.env.VITE_SUITE_URL || ''
function fileUrl(uuid: string) {
  return `${suiteUrl}/orders/file/${uuid}`
}

function getExistingFilesForField(fieldName: string, index?: number | string): OrderFile[] {
  if (!props.orderFiles?.length) return []
  const base = String(fieldName)
    .replace(/\{\{index\}\}/g, '')
    .replace(/\[\{\{index\}\}\].*$/, '')
  const idx = index === undefined ? undefined : Number(index)
  const fieldMatches = (f: OrderFile) => f.fieldName === base || f.fieldName.startsWith(base + '[') || f.fieldName.includes(base)
  return props.orderFiles.filter((f) => fieldMatches(f) && (idx === undefined || (f.orderIndex ?? 0) === idx))
}

const model = defineModel<Record<string, any>>({ required: true })

const activeSectionId = ref<string | null>(null)
const activeFieldId = ref<string | null>(null)
const errors = ref<Record<string, string>>({})

function setSectionActive(sectionId: string, isActive: boolean) {
  if (isActive) {
    activeSectionId.value = sectionId
  } else if (activeSectionId.value === sectionId) {
    activeSectionId.value = null
  }
}

function setFieldActive(fieldId: string | null) {
  activeFieldId.value = fieldId
}

function resolveIndexInString(str: string, index: number | string | undefined): string {
  if (index === undefined || !str.includes('{{index}}')) return str
  return str.replace(/\{\{index\}\}/g, String(index))
}

function shouldShow(element: FormField | FormSection, item: any = null, index?: number | string): boolean {
  const resolvedIndex = index ?? props.repeatableIndex
  const context = item || model.value
  const fallbackContext = item && model.value ? model.value : undefined
  const resolveIndex = (str: string, idx?: number | string) =>
    resolveIndexInString(str, idx ?? resolvedIndex)
  return evaluateCondition(
    element.condition,
    context,
    resolveIndex,
    resolvedIndex,
    fallbackContext,
  )
}

function getOptions(field: FormField, index?: number | string) {
  if (!field.options) return []
  if (Array.isArray(field.options)) {
    return field.options.map((opt, optIdx) => ({
      id: String(optIdx),
      value:
        index !== undefined && typeof opt.value === 'string' && opt.value.includes('{{index}}')
          ? opt.value.replace(/\{\{index\}\}/g, String(index))
          : opt.value,
      label:
        index !== undefined && typeof opt.label === 'string' && opt.label.includes('{{index}}')
          ? opt.label.replace(/\{\{index\}\}/g, String(index))
          : opt.label,
    }))
  }
  return []
}

function formatLabel(label: string | undefined, name: string | undefined, index?: number | string): string {
  const text = label || name || ''
  if (index !== undefined) {
    // On remplace {{index}} par l'index affiché (1, 2, 3...) pour l'utilisateur
    const displayIndex = Number(index) + 1
    return text.replace(/\{\{index\}\}/g, String(displayIndex))
  }
  return text
}

function formatFieldName(name: string, index?: number | string): string {
  if (index !== undefined && name.includes('{{index}}')) {
    return name.replace(/\{\{index\}\}/g, String(index))
  }
  return name
}

function addRepeatableItem(section: FormSection) {
  const sectionId = section.id
  if (!model.value[sectionId]) {
    model.value[sectionId] = []
  }

  const newIndex = model.value[sectionId].length

  const fields = (section.fields || []).filter((f) => f.type !== 'section' && f.type !== 'under-section' && !f.repeatable && !f.fields)

  const sectionFields = (section.fields || []).filter((f) => f.type === 'section' || f.type === 'under-section' || f.repeatable || f.fields)

  const extraSections: FormSection[] = sectionFields.map((f) => ({
    id: f.id,
    name: f.label || f.name,
    fields: f.fields || [],
    repeatable: f.repeatable,
    repeatableConfig: f.repeatableConfig,
    condition: f.condition,
    sections: f.sections,
  }))

  const newItem = {} as any
  fields.forEach((f) => {
    const resolvedName = formatFieldName(f.name, newIndex)
    newItem[resolvedName] = f.defaultValue ?? (f.type === 'switch' ? false : f.multiple ? [] : null)
  })

  function initializeSubSections(secs: FormSection[], target: any, idx: number) {
    secs.forEach((s) => {
      if (s.repeatable) {
        const resolvedId = formatFieldName(s.id, idx)
        target[resolvedId] = []
      } else {
        ;(s.fields || []).forEach((f) => {
          if (f.type === 'section' || f.type === 'under-section' || f.repeatable || f.fields) {
            const subSec: FormSection = {
              id: f.id,
              name: f.label || f.name,
              fields: f.fields || [],
              repeatable: f.repeatable,
              repeatableConfig: f.repeatableConfig,
              sections: f.sections,
            }
            if (subSec.repeatable) {
              const resolvedId = formatFieldName(subSec.id, idx)
              target[resolvedId] = []
            } else {
              initializeSubSections([subSec], target, idx)
            }
          } else {
            const resolvedName = formatFieldName(f.name, idx)
            target[resolvedName] = f.defaultValue ?? (f.type === 'switch' ? false : f.multiple ? [] : null)
          }
        })
        if (s.sections) {
          initializeSubSections(s.sections, target, idx)
        }
      }
    })
  }

  initializeSubSections(extraSections, newItem, newIndex)

  model.value[sectionId].push(newItem)
}

function removeRepeatableItem(sectionId: string, index: number | string) {
  if (model.value[sectionId]) {
    model.value[sectionId].splice(Number(index), 1)
  }
}

function handleFileChange(fieldName: string, files: FileList | null) {
  model.value[fieldName] = files
}

function validate(): boolean {
  const newErrors: Record<string, string> = {}

  function validateField(field: FormField, currentModel: any, path: string = '', index?: number) {
    // Si le champ ne doit pas être affiché, on ne le valide pas
    if (!shouldShow(field, currentModel, index)) return

    const fieldKey = formatFieldName(field.name, index)
    let fieldValue = undefined
    if (currentModel) {
      if (fieldKey.includes('.')) {
        fieldValue = fieldKey.split('.').reduce((acc: any, part: string) => acc && acc[part], currentModel)
      } else {
        fieldValue = currentModel[fieldKey]
      }
    }
    const fullPath = path ? `${path}.${fieldKey}` : fieldKey

    // Validation si requis
    if (field.required) {
      const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0)

      if (isEmpty) {
        newErrors[fullPath] = 'Ce champ est obligatoire'
      }
    }

    // Récursion si section/repeatable
    if (field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields) {
      if (field.repeatable) {
        const repeatableId = formatFieldName(field.id || field.name, index)
        const items = currentModel ? currentModel[repeatableId] || [] : []
        items.forEach((item: any, subIndex: number) => {
          const itemPath = `${path ? path + '.' : ''}${repeatableId}[${subIndex}]`
          ;(field.fields || []).forEach((subField) => {
            validateField(subField, item, itemPath, subIndex)
          })
          if (field.sections) {
            field.sections.forEach((subSec) => {
              validateSection(subSec, item, itemPath, subIndex)
            })
          }
        })
      } else {
        // Section simple, le modèle est le même ou imbriqué si c'est un champ de type section
        const resolvedId = formatFieldName(field.id || '', index)
        const hasId = !!(resolvedId && currentModel && currentModel[resolvedId])
        const subModel = hasId ? currentModel[resolvedId] : currentModel
        const subPath = hasId ? (path ? `${path}.${resolvedId}` : resolvedId) : path
        ;(field.fields || []).forEach((subField) => {
          validateField(subField, subModel, subPath, index)
        })
        if (field.sections) {
          field.sections.forEach((subSec) => {
            validateSection(subSec, subModel, subPath, index)
          })
        }
      }
    }
  }

  function validateSection(section: FormSection, currentModel: any = model.value, path: string = '', index?: number) {
    // Si la section ne doit pas être affichée, on ne valide ni ses champs ni ses sous-sections
    if (!shouldShow(section, currentModel, index)) return

    if (section.repeatable) {
      const items = currentModel ? currentModel[section.id] || [] : []
      items.forEach((item: any, idx: number) => {
        const itemPath = path ? `${path}.${section.id}[${idx}]` : `${section.id}[${idx}]`
        ;(section.fields || []).forEach((field) => {
          validateField(field, item, itemPath, idx)
        })
        if (section.sections) {
          section.sections.forEach((subSec) => {
            validateSection(subSec, item, itemPath, idx)
          })
        }
      })
    } else {
      ;(section.fields || []).forEach((field) => {
        validateField(field, currentModel, path, index)
      })
      if (section.sections) {
        section.sections.forEach((subSec) => validateSection(subSec, currentModel, path, index))
      }
    }
  }

  props.schema.sections.forEach((section) => validateSection(section))

  errors.value = newErrors
  return Object.keys(newErrors).length === 0
}

defineExpose({
  validate,
  errors,
})
</script>

<template>
  <div class="space-y-4">
    <template v-for="section in schema.sections" :key="section.id">
      <div
        v-if="shouldShow(section, model, props.repeatableIndex)"
        :class="[props.embedSections ? 'space-y-4' : 'collapse collapse-arrow bg-base-100 border border-base-200']"
        :style="{ zIndex: activeSectionId === section.id ? 1000 : 'auto' }"
      >
        <template v-if="!props.embedSections">
          <input type="checkbox" :checked="true" />
          <div class="collapse-title text-lg font-bold flex justify-between items-center gap-2">
            {{ section.name || section.label || '\u00A0' }}
            <span v-if="section.repeatable && model[section.id]" class="badge badge-sm badge-ghost">
              {{ model[section.id].length }}
            </span>
          </div>
        </template>

        <div :class="props.embedSections ? 'space-y-4' : 'collapse-content space-y-4'">
          <div v-if="!section.repeatable" class="space-y-4 pt-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <template v-for="field in section.fields" :key="field.id">
                <div
                  v-if="shouldShow(field, model, props.repeatableIndex)"
                  :class="[
                    field.type === 'wysiwyg' ||
                    field.type === 'section' ||
                    field.type === 'under-section' ||
                    field.repeatable ||
                    field.fields ||
                    field.newRow
                      ? 'md:col-span-2'
                      : 'md:col-span-1',
                    activeFieldId === field.id ? 'z-[1001] relative' : 'z-auto',
                  ]"
                  class="w-full"
                >
                  <!-- Rendu dynamique selon le type -->
                  <template v-if="field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields">
                    <!-- On traite le champ comme une sous-section imbriquée -->
                    <div v-if="shouldShow(field, model, props.repeatableIndex)" class="mt-4 border-l-2 border-base-200 pl-4 py-2 w-full">
                      <div
                        v-if="field.label || field.name || (field.repeatable && model[field.id]?.length)"
                        class="font-semibold text-base mb-2 flex items-center gap-2"
                      >
                        {{ field.label || field.name || '' }}
                        <span v-if="field.repeatable && model[field.id]" class="badge badge-sm badge-ghost">
                          {{ model[field.id].length }}
                        </span>
                      </div>

                      <div v-if="!field.repeatable" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <template v-for="subField in field.fields" :key="subField.id">
                          <div
                            v-if="shouldShow(subField, model, props.repeatableIndex)"
                            :class="
                              subField.type === 'wysiwyg' ||
                              subField.type === 'section' ||
                              subField.type === 'under-section' ||
                              subField.repeatable ||
                              subField.fields ||
                              subField.newRow
                                ? 'md:col-span-2'
                                : 'md:col-span-1'
                            "
                            class="w-full"
                          >
                            <TextInput
                              v-if="subField.type === 'text' || subField.type === 'textarea'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :label="subField.label"
                              :placeholder="subField.placeholder"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                            />
                            <WysiwygInput
                              v-else-if="subField.type === 'wysiwyg'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :name="formatFieldName(subField.name, props.repeatableIndex)"
                              :label="subField.label"
                              :placeholder="subField.placeholder"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                            />
                            <NumberInput
                              v-else-if="subField.type === 'number'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :label="subField.label"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :min="subField.validation?.min"
                              :step="subField.validation?.step ?? 'any'"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                            />
                            <DateInput
                              v-else-if="subField.type === 'date'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :label="subField.label"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                            />
                            <SwitchInput
                              v-else-if="subField.type === 'switch'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :label="subField.label"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                            />
                            <OrderFileInput
                              v-else-if="subField.type === 'file'"
                              :name="formatFieldName(subField.name, props.repeatableIndex)"
                              :label="subField.label"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :accept="subField.accept"
                              :multiple="subField.multiple"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                              :existing-files="getExistingFilesForField(subField.name, props.repeatableIndex)"
                              @change="(files) => (model[formatFieldName(subField.name, props.repeatableIndex)] = files)"
                              @delete-file="handleDeleteFile"
                            />
                            <SelectInput
                              v-else-if="subField.type === 'select'"
                              v-model="model[formatFieldName(subField.name, props.repeatableIndex)]"
                              :label="subField.label"
                              :options="getOptions(subField, props.repeatableIndex)"
                              :required="subField.required"
                              :disabled="isFieldDisabled(subField)"
                              :multiple="subField.multiple"
                              :placeholder="subField.placeholder"
                              :error-message="errors[formatFieldName(subField.name, props.repeatableIndex)]"
                              @open="
                                () => {
                                  setSectionActive(section.id, true)
                                  setFieldActive(subField.id)
                                }
                              "
                              @close="
                                () => {
                                  setSectionActive(section.id, false)
                                  setFieldActive(null)
                                }
                              "
                            />
                          </div>
                        </template>
                      </div>

                      <div v-else class="space-y-4">
                        <div
                          v-for="(subItem, subIndex) in model[field.id]"
                          :key="subIndex"
                          class="p-3 bg-base-200/30 rounded border border-base-200 relative"
                        >
                          <button
                            v-if="!props.fieldsDisabled"
                            class="btn btn-circle btn-xs btn-ghost absolute top-1 right-1 text-error"
                            @click="removeRepeatableItem(field.id, subIndex)"
                          >
                            ✕
                          </button>
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <template v-for="subField in field.fields" :key="`${formatFieldName(subField.id, subIndex)}-${subIndex}`">
                              <div
                                v-if="shouldShow(subField, subItem, subIndex)"
                                :class="[
                                  subField.type === 'wysiwyg' ||
                                  subField.type === 'section' ||
                                  subField.type === 'under-section' ||
                                  subField.repeatable ||
                                  subField.fields ||
                                  subField.newRow
                                    ? 'md:col-span-2'
                                    : 'md:col-span-1',
                                  activeFieldId === subField.id ? 'z-[1001] relative' : 'z-auto',
                                ]"
                                class="w-full"
                              >
                                <template
                                  v-if="subField.type === 'section' || subField.type === 'under-section' || subField.repeatable || subField.fields"
                                >
                                  <div
                                    v-if="shouldShow(subField, subItem, subIndex)"
                                    class="mt-4 border-l-2 border-base-200 pl-4 py-2 w-full md:col-span-2"
                                  >
                                    <div
                                      v-if="subField.label || subField.name || (subField.repeatable && subItem[subField.id]?.length)"
                                      class="font-semibold text-base mb-2 flex items-center gap-2"
                                    >
                                      {{ formatLabel(subField.label, subField.name, subIndex) }}
                                      <span v-if="subField.repeatable && subItem[subField.id]" class="badge badge-sm badge-ghost">
                                        {{ subItem[subField.id].length }}
                                      </span>
                                    </div>

                                    <div v-if="!subField.repeatable" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <template v-for="ssField in subField.fields" :key="ssField.id">
                                        <div
                                          v-if="shouldShow(ssField, subItem, subIndex)"
                                          :class="[
                                            ssField.type === 'wysiwyg' ||
                                            ssField.type === 'section' ||
                                            ssField.type === 'under-section' ||
                                            ssField.repeatable ||
                                            ssField.fields ||
                                            ssField.newRow
                                              ? 'md:col-span-2'
                                              : 'md:col-span-1',
                                            activeFieldId === ssField.id ? 'z-[1001] relative' : 'z-auto',
                                          ]"
                                          class="w-full"
                                        >
                                          <template
                                            v-if="
                                              ssField.type === 'section' || ssField.type === 'under-section' || ssField.repeatable || ssField.fields
                                            "
                                          >
                                            <div
                                              v-if="shouldShow(ssField, subItem, subIndex)"
                                              class="mt-4 border-l-2 border-base-200 pl-4 py-2 w-full md:col-span-2"
                                            >
                                              <div
                                                v-if="ssField.label || ssField.name || (ssField.repeatable && subItem[ssField.id]?.length)"
                                                class="font-semibold text-base mb-2 flex items-center gap-2"
                                              >
                                                {{ formatLabel(ssField.label, ssField.name, subIndex) }}
                                                <span v-if="ssField.repeatable && subItem[ssField.id]" class="badge badge-sm badge-ghost">
                                                  {{ subItem[ssField.id].length }}
                                                </span>
                                              </div>
                                              <!-- Ici on pourrait encore récurser ou appeler DynamicForm, mais on va limiter pour l'instant ou utiliser DynamicForm -->
                                              <DynamicForm
                                                v-model="model[field.id][subIndex]"
                                                :schema="{
                                                  ...schema,
                                                  sections: [
                                                    {
                                                      id: ssField.id,
                                                      name: ssField.label || ssField.name,
                                                      fields: ssField.fields || [],
                                                      sections: ssField.sections || [],
                                                      repeatable: ssField.repeatable,
                                                      repeatableConfig: ssField.repeatableConfig,
                                                      condition: ssField.condition,
                                                    },
                                                  ],
                                                }"
                                                :order-files="orderFiles"
                                                :fields-disabled="props.fieldsDisabled"
                                                embed-sections
                                                @delete-file="handleDeleteFile"
                                              />
                                            </div>
                                          </template>
                                          <template v-else>
                                            <TextInput
                                              v-if="ssField.type === 'text' || ssField.type === 'textarea'"
                                              v-model="model[field.id][subIndex][formatFieldName(ssField.name, subIndex)]"
                                              :label="ssField.label"
                                              :placeholder="ssField.placeholder"
                                              :required="ssField.required"
                                              :disabled="isFieldDisabled(ssField)"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                            />
                                            <NumberInput
                                              v-else-if="ssField.type === 'number'"
                                              v-model="model[field.id][subIndex][formatFieldName(ssField.name, subIndex)]"
                                              :label="ssField.label"
                                              :required="ssField.required"
                                              :disabled="isFieldDisabled(ssField)"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                            />
                                            <DateInput
                                              v-else-if="ssField.type === 'date'"
                                              v-model="model[field.id][subIndex][formatFieldName(ssField.name, subIndex)]"
                                              :label="ssField.label"
                                              :required="ssField.required"
                                              :disabled="isFieldDisabled(ssField)"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                            />
                                            <SwitchInput
                                              v-else-if="ssField.type === 'switch'"
                                              v-model="model[field.id][subIndex][formatFieldName(ssField.name, subIndex)]"
                                              :label="ssField.label"
                                              :disabled="isFieldDisabled(ssField)"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                            />
                                            <OrderFileInput
                                              v-else-if="ssField.type === 'file'"
                                              :name="formatFieldName(ssField.name, subIndex)"
                                              :label="ssField.label"
                                              :required="ssField.required"
                                              :disabled="isFieldDisabled(ssField)"
                                              :accept="ssField.accept"
                                              :multiple="ssField.multiple"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                              :existing-files="getExistingFilesForField(ssField.name, subIndex)"
                                              @change="(files) => (model[field.id][subIndex][formatFieldName(ssField.name, subIndex)] = files)"
                                              @delete-file="handleDeleteFile"
                                            />
                                            <SelectInput
                                              v-else-if="ssField.type === 'select'"
                                              v-model="model[field.id][subIndex][formatFieldName(ssField.name, subIndex)]"
                                              :label="ssField.label"
                                              :options="getOptions(ssField, subIndex)"
                                              :required="ssField.required"
                                              :disabled="isFieldDisabled(ssField)"
                                              :multiple="ssField.multiple"
                                              :placeholder="ssField.placeholder"
                                              :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(ssField.name, subIndex)}`]"
                                              @open="
                                                () => {
                                                  setSectionActive(section.id, true)
                                                  setFieldActive(ssField.id)
                                                }
                                              "
                                              @close="
                                                () => {
                                                  setSectionActive(section.id, false)
                                                  setFieldActive(null)
                                                }
                                              "
                                            />
                                          </template>
                                        </div>
                                      </template>
                                    </div>
                                    <div v-else class="space-y-4">
                                      <div
                                        v-for="(ssItem, ssIndex) in subItem[subField.id]"
                                        :key="ssIndex"
                                        class="p-3 bg-base-300/30 rounded border border-base-300 relative"
                                      >
                                        <button
                                          v-if="!props.fieldsDisabled"
                                          class="btn btn-circle btn-xs btn-ghost absolute top-1 right-1 text-error"
                                          @click="subItem[subField.id].splice(ssIndex, 1)"
                                        >
                                          ✕
                                        </button>
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <template v-for="ssField in subField.fields" :key="ssField.id">
                                            <div
                                              v-if="shouldShow(ssField, ssItem)"
                                              :class="[
                                                ssField.type === 'wysiwyg' ||
                                                ssField.type === 'section' ||
                                                ssField.type === 'under-section' ||
                                                ssField.repeatable ||
                                                ssField.fields ||
                                                ssField.newRow
                                                  ? 'md:col-span-2'
                                                  : 'md:col-span-1',
                                                activeFieldId === ssField.id ? 'z-[1001] relative' : 'z-auto',
                                              ]"
                                              class="w-full"
                                            >
                                              <TextInput
                                                v-if="ssField.type === 'text' || ssField.type === 'textarea'"
                                                v-model="model[field.id][subIndex][subField.id][ssIndex][ssField.name]"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :placeholder="ssField.placeholder"
                                                :required="ssField.required"
                                                :disabled="isFieldDisabled(ssField)"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                              />
                                              <NumberInput
                                                v-else-if="ssField.type === 'number'"
                                                v-model="model[field.id][subIndex][subField.id][ssIndex][ssField.name]"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :required="ssField.required"
                                                :disabled="isFieldDisabled(ssField)"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                              />
                                              <DateInput
                                                v-else-if="ssField.type === 'date'"
                                                v-model="model[field.id][subIndex][subField.id][ssIndex][ssField.name]"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :required="ssField.required"
                                                :disabled="isFieldDisabled(ssField)"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                              />
                                              <SwitchInput
                                                v-else-if="ssField.type === 'switch'"
                                                v-model="model[field.id][subIndex][subField.id][ssIndex][ssField.name]"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :disabled="isFieldDisabled(ssField)"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                              />
                                              <SelectInput
                                                v-else-if="ssField.type === 'select'"
                                                v-model="model[field.id][subIndex][subField.id][ssIndex][ssField.name]"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :options="getOptions(ssField)"
                                                :required="ssField.required"
                                                :disabled="isFieldDisabled(ssField)"
                                                :multiple="ssField.multiple"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                                @open="
                                                  () => {
                                                    setSectionActive(section.id, true)
                                                    setFieldActive(ssField.id)
                                                  }
                                                "
                                                @close="
                                                  () => {
                                                    setSectionActive(section.id, false)
                                                    setFieldActive(null)
                                                  }
                                                "
                                              />
                                              <OrderFileInput
                                                v-else-if="ssField.type === 'file'"
                                                :name="formatFieldName(ssField.name, ssIndex)"
                                                :label="formatLabel(ssField.label, undefined, ssIndex)"
                                                :required="ssField.required"
                                                :disabled="isFieldDisabled(ssField)"
                                                :accept="ssField.accept"
                                                :multiple="ssField.multiple"
                                                :error-message="errors[`${field.id}[${subIndex}].${subField.id}[${ssIndex}].${ssField.name}`]"
                                                :existing-files="getExistingFilesForField(ssField.name, ssIndex)"
                                                @change="(files) => (model[field.id][subIndex][subField.id][ssIndex][ssField.name] = files)"
                                                @delete-file="handleDeleteFile"
                                              />
                                            </div>
                                          </template>
                                        </div>
                                      </div>
                                      <div v-if="!props.fieldsDisabled" class="flex justify-center">
                                        <button class="btn btn-outline btn-xs btn-primary gap-1" @click="subItem[subField.id].push({})">
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            class="w-3 h-3"
                                          >
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                          </svg>
                                          {{ subField.repeatableConfig?.addButtonLabel || 'Ajouter' }}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </template>

                                <TextInput
                                  v-else-if="subField.type === 'text' || subField.type === 'textarea'"
                                  v-model="model[field.id][subIndex][formatFieldName(subField.name, subIndex)]"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :placeholder="subField.placeholder"
                                  :required="subField.required"
                                  :disabled="isFieldDisabled(subField)"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                />
                                <NumberInput
                                  v-else-if="subField.type === 'number'"
                                  v-model="model[field.id][subIndex][formatFieldName(subField.name, subIndex)]"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :required="subField.required"
                                  :disabled="isFieldDisabled(subField)"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                />
                                <SwitchInput
                                  v-else-if="subField.type === 'switch'"
                                  v-model="model[field.id][subIndex][formatFieldName(subField.name, subIndex)]"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :disabled="isFieldDisabled(subField)"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                />
                                <DateInput
                                  v-else-if="subField.type === 'date'"
                                  v-model="model[field.id][subIndex][formatFieldName(subField.name, subIndex)]"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :required="subField.required"
                                  :disabled="isFieldDisabled(subField)"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                />
                                <OrderFileInput
                                  v-else-if="subField.type === 'file'"
                                  :name="formatFieldName(subField.name, subIndex)"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :required="subField.required"
                                  :disabled="isFieldDisabled(subField)"
                                  :accept="subField.accept"
                                  :multiple="subField.multiple"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                  :existing-files="getExistingFilesForField(subField.name, subIndex)"
                                  @change="(files) => (model[field.id][subIndex][formatFieldName(subField.name, subIndex)] = files)"
                                  @delete-file="handleDeleteFile"
                                />
                                <SelectInput
                                  v-else-if="subField.type === 'select'"
                                  v-model="model[field.id][subIndex][formatFieldName(subField.name, subIndex)]"
                                  :label="formatLabel(subField.label, undefined, subIndex)"
                                  :options="getOptions(subField, subIndex)"
                                  :required="subField.required"
                                  :disabled="isFieldDisabled(subField)"
                                  :multiple="subField.multiple"
                                  :placeholder="subField.placeholder"
                                  :error-message="errors[`${field.id}[${subIndex}].${formatFieldName(subField.name, subIndex)}`]"
                                  @open="
                                    () => {
                                      setSectionActive(section.id, true)
                                      setFieldActive(subField.id)
                                    }
                                  "
                                  @close="
                                    () => {
                                      setSectionActive(section.id, false)
                                      setFieldActive(null)
                                    }
                                  "
                                />
                              </div>
                            </template>
                          </div>
                        </div>
                        <div v-if="!props.fieldsDisabled" class="flex justify-center">
                          <button
                            class="btn btn-outline btn-xs btn-primary gap-1"
                            @click="addRepeatableItem({ ...field, id: field.id, fields: field.fields } as any)"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              class="w-3 h-3"
                            >
                              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            {{ field.repeatableConfig?.addButtonLabel || 'Ajouter' }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>

                  <TextInput
                    v-else-if="field.type === 'text' || field.type === 'textarea'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :error-message="errors[field.name]"
                  />
                  <WysiwygInput
                    v-else-if="field.type === 'wysiwyg'"
                    v-model="model[field.name]"
                    :name="field.name"
                    :label="field.label"
                    :placeholder="field.placeholder"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :error-message="errors[field.name]"
                  />
                  <NumberInput
                    v-else-if="field.type === 'number'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :min="field.validation?.min"
                    :step="field.validation?.step ?? (field.type === 'number' && field.name.includes('amount') ? '0.01' : 'any')"
                    :error-message="errors[field.name]"
                  />
                  <DateInput
                    v-else-if="field.type === 'date'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :error-message="errors[field.name]"
                  />
                  <RadioInput
                    v-else-if="field.type === 'radio'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :options="getOptions(field)"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :error-message="errors[field.name]"
                  />
                  <SelectInput
                    v-else-if="field.type === 'select'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :options="getOptions(field)"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :multiple="field.multiple"
                    :placeholder="field.placeholder"
                    :error-message="errors[field.name]"
                    @open="setSectionActive(section.id, true)"
                    @close="setSectionActive(section.id, false)"
                  />
                  <OrderFileInput
                    v-else-if="field.type === 'file'"
                    :name="field.name"
                    :label="field.label"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :accept="field.accept"
                    :multiple="field.multiple"
                    :error-message="errors[field.name]"
                    :existing-files="getExistingFilesForField(field.name)"
                    @change="(files) => handleFileChange(field.name, files)"
                    @delete-file="handleDeleteFile"
                  />
                  <SwitchInput
                    v-else-if="field.type === 'switch'"
                    v-model="model[field.name]"
                    :label="field.label"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :error-message="errors[field.name]"
                  />
                  <div v-else-if="field.type === 'checkbox-group'">
                    <TextInput v-model="model[field.name]" :label="field.label" :placeholder="field.type" :disabled="isFieldDisabled(field)" />
                  </div>
                </div>
              </template>
            </div>

            <!-- Recursion for sub-sections -->
            <template v-if="section.sections && section.sections.length > 0">
              <div class="space-y-4 ml-4 md:ml-8 border-l-2 border-base-200 pl-4 md:pl-6 py-2">
                <template v-for="subSection in section.sections" :key="subSection.id">
                  <div v-if="shouldShow(subSection)">
                    <div
                      v-if="subSection.name || subSection.label || (subSection.repeatable && model[subSection.id]?.length)"
                      class="font-semibold text-base mb-2 flex items-center gap-2"
                    >
                      {{ subSection.name || subSection.label || '' }}
                      <span v-if="subSection.repeatable && model[subSection.id]" class="badge badge-sm badge-ghost">
                        {{ model[subSection.id].length }}
                      </span>
                    </div>

                    <div v-if="!subSection.repeatable" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <template v-for="field in subSection.fields" :key="field.id">
                        <div
                          v-if="shouldShow(field)"
                          :class="field.type === 'wysiwyg' || field.newRow ? 'md:col-span-2' : 'md:col-span-1'"
                          class="w-full"
                        >
                          <template v-if="field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields">
                            <div v-if="shouldShow(field)" class="mt-4 border-l-2 border-base-200 pl-4 py-2 w-full md:col-span-2">
                              <div
                                v-if="field.label || field.name || (field.repeatable && model[field.id]?.length)"
                                class="font-semibold text-base mb-2 flex items-center gap-2"
                              >
                                {{ field.label || field.name || '' }}
                                <span v-if="field.repeatable && model[field.id]" class="badge badge-sm badge-ghost">
                                  {{ model[field.id].length }}
                                </span>
                              </div>
                              <DynamicForm
                                v-model="model"
                                :schema="{
                                  ...schema,
                                  sections: [
                                    {
                                      id: field.id,
                                      name: field.label,
                                      fields: field.fields || [],
                                      sections: field.sections || [],
                                      repeatable: field.repeatable,
                                      repeatableConfig: field.repeatableConfig,
                                      condition: field.condition,
                                    },
                                  ],
                                }"
                                :order-files="orderFiles"
                                :fields-disabled="props.fieldsDisabled"
                                embed-sections
                                @delete-file="handleDeleteFile"
                              />
                            </div>
                          </template>
                          <template v-else>
                            <TextInput
                              v-if="field.type === 'text' || field.type === 'textarea'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :placeholder="field.placeholder"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :error-message="errors[field.name]"
                            />
                            <WysiwygInput
                              v-else-if="field.type === 'wysiwyg'"
                              v-model="model[field.name]"
                              :name="field.name"
                              :label="field.label"
                              :placeholder="field.placeholder"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :error-message="errors[field.name]"
                            />
                            <NumberInput
                              v-else-if="field.type === 'number'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :min="field.validation?.min"
                              :step="field.validation?.step ?? (field.type === 'number' && field.name.includes('amount') ? '0.01' : 'any')"
                              :error-message="errors[field.name]"
                            />
                            <DateInput
                              v-else-if="field.type === 'date'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :error-message="errors[field.name]"
                            />
                            <RadioInput
                              v-else-if="field.type === 'radio'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :options="getOptions(field)"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :error-message="errors[field.name]"
                            />
                            <SelectInput
                              v-else-if="field.type === 'select'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :options="getOptions(field)"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :multiple="field.multiple"
                              :placeholder="field.placeholder"
                              :error-message="errors[field.name]"
                              @open="setSectionActive(subSection.id, true)"
                              @close="setSectionActive(subSection.id, false)"
                            />
                            <OrderFileInput
                              v-else-if="field.type === 'file'"
                              :name="field.name"
                              :label="field.label"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :accept="field.accept"
                              :multiple="field.multiple"
                              :error-message="errors[field.name]"
                              :existing-files="getExistingFilesForField(field.name)"
                              @change="(files) => handleFileChange(field.name, files)"
                              @delete-file="handleDeleteFile"
                            />
                            <SwitchInput
                              v-else-if="field.type === 'switch'"
                              v-model="model[field.name]"
                              :label="field.label"
                              :required="field.required"
                              :disabled="isFieldDisabled(field)"
                              :error-message="errors[field.name]"
                            />
                          </template>
                        </div>
                      </template>
                    </div>

                    <div v-else class="space-y-4">
                      <div
                        v-for="(item, index) in model[subSection.id]"
                        :key="index"
                        class="p-4 bg-base-200/50 rounded-lg relative border border-base-300"
                      >
                        <button
                          v-if="!props.fieldsDisabled"
                          class="btn btn-circle btn-xs btn-ghost absolute top-2 right-2 text-error hover:bg-error/20"
                          @click="removeRepeatableItem(subSection.id, index)"
                        >
                          ✕
                        </button>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <template v-for="field in subSection.fields" :key="field.id">
                            <div
                              v-if="shouldShow(field, item, index)"
                              :class="
                                field.type === 'wysiwyg' ||
                                field.type === 'section' ||
                                field.type === 'under-section' ||
                                field.repeatable ||
                                field.fields ||
                                field.newRow
                                  ? 'md:col-span-2'
                                  : 'md:col-span-1'
                              "
                              class="w-full"
                            >
                              <TextInput
                                v-if="field.type === 'text' || field.type === 'textarea'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :label="formatLabel(field.label, undefined, index)"
                                :placeholder="field.placeholder"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :error-message="errors[`${subSection.id}[${index}].${field.name}`]"
                              />
                              <WysiwygInput
                                v-else-if="field.type === 'wysiwyg'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :name="formatFieldName(field.name, index)"
                                :label="formatLabel(field.label, undefined, index)"
                                :placeholder="field.placeholder"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :error-message="errors[`${subSection.id}[${index}].${field.name}`]"
                              />
                              <NumberInput
                                v-else-if="field.type === 'number'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :label="formatLabel(field.label, undefined, index)"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :min="field.validation?.min"
                                :step="field.validation?.step ?? (field.type === 'number' && field.name.includes('amount') ? '0.01' : 'any')"
                                :error-message="errors[`${subSection.id}[${index}].${field.name}`]"
                              />
                              <DateInput
                                v-else-if="field.type === 'date'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :label="formatLabel(field.label, undefined, index)"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :error-message="errors[`${subSection.id}[${index}].${field.name}`]"
                              />
                              <OrderFileInput
                                v-else-if="field.type === 'file'"
                                :name="formatFieldName(field.name, index)"
                                :label="formatLabel(field.label, undefined, index)"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :accept="field.accept"
                                :multiple="field.multiple"
                                :error-message="errors[`${subSection.id}[${index}].${formatFieldName(field.name, index)}`]"
                                :existing-files="getExistingFilesForField(field.name, index)"
                                @change="(files) => (model[subSection.id][index][formatFieldName(field.name, index)] = files)"
                                @delete-file="handleDeleteFile"
                              />
                              <SelectInput
                                v-else-if="field.type === 'select'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :label="formatLabel(field.label, undefined, index)"
                                :options="getOptions(field, index)"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :multiple="field.multiple"
                                :placeholder="field.placeholder"
                                :error-message="errors[`${subSection.id}[${index}].${formatFieldName(field.name, index)}`]"
                                @open="setSectionActive(subSection.id, true)"
                                @close="setSectionActive(subSection.id, false)"
                              />
                              <SwitchInput
                                v-else-if="field.type === 'switch'"
                                v-model="model[subSection.id][index][formatFieldName(field.name, index)]"
                                :label="formatLabel(field.label, undefined, index)"
                                :required="field.required"
                                :disabled="isFieldDisabled(field)"
                                :error-message="errors[`${subSection.id}[${index}].${field.name}`]"
                              />
                            </div>
                          </template>
                        </div>
                      </div>

                      <div v-if="!props.fieldsDisabled" class="flex justify-center py-2">
                        <button class="btn btn-outline btn-sm btn-primary gap-2" @click="addRepeatableItem(subSection)">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                          {{ subSection.repeatableConfig?.addButtonLabel || 'Ajouter' }}
                        </button>
                      </div>
                    </div>

                    <!-- Further recursion if needed -->
                    <template v-if="subSection.sections && subSection.sections.length > 0">
                      <div class="space-y-4 ml-4 md:ml-8 border-l-2 border-base-200 pl-4 md:pl-6 py-2">
                        <DynamicForm
                          v-model="model"
                          :schema="{ ...schema, sections: subSection.sections }"
                          :order-files="orderFiles"
                          :fields-disabled="props.fieldsDisabled"
                          @delete-file="handleDeleteFile"
                        />
                      </div>
                    </template>
                  </div>
                </template>
              </div>
            </template>
          </div>

          <div v-else-if="isFileOnlyRepeatableSection(section)" class="space-y-2 pt-4">
            <div
              v-if="section.id === 'annexes_section' && getExistingFilesForField('files_annexe').length > 0"
              class="space-y-2 p-3 bg-base-200/50 rounded border border-base-300 mb-4"
            >
              <div class="font-semibold text-sm mb-2">Fichier Annexe</div>
              <div class="space-y-2">
                <div
                  v-for="f in getExistingFilesForField('files_annexe')"
                  :key="f.uuid"
                  class="flex items-center justify-between gap-2 p-2 bg-base-100 rounded border"
                >
                  <a :href="fileUrl(f.uuid)" target="_blank" class="link link-hover text-sm truncate flex-1 min-w-0">
                    {{ f.fileName }}
                  </a>
                  <button v-if="!props.fieldsDisabled" type="button" class="btn btn-ghost btn-xs text-error shrink-0" @click="emit('deleteFile', f.uuid)">Supprimer</button>
                </div>
              </div>
            </div>
            <div
              v-for="(item, index) in model[section.id] || []"
              :key="index"
              class="flex items-center gap-2 p-2 bg-base-200/50 rounded border border-base-300"
            >
              <div class="flex-1 min-w-0">
                <template v-for="field in section.fields" :key="field.id">
                  <OrderFileInput
                    v-if="field.type === 'file' && shouldShow(field, item, index)"
                    :label="formatLabel(field.label, undefined, index)"
                    :name="formatFieldName(field.name, index)"
                    :required="field.required"
                    :disabled="isFieldDisabled(field)"
                    :accept="field.accept"
                    :multiple="field.multiple"
                    :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                    :existing-files="section.id === 'annexes_section' ? [] : getExistingFilesForField(field.name, index)"
                    @change="(files) => (model[section.id][index][formatFieldName(field.name, index)] = files)"
                    @delete-file="handleDeleteFileWithContext(section.id, Number(index))"
                  />
                </template>
              </div>
              <button
                v-if="!props.fieldsDisabled"
                class="btn btn-ghost btn-xs text-error shrink-0"
                @click="removeRepeatableItem(section.id, index)"
              >
                ✕
              </button>
            </div>
            <div v-if="!props.fieldsDisabled" class="flex justify-center py-2">
              <button class="btn btn-outline btn-sm btn-primary gap-2" @click="addRepeatableItem(section)">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {{ section.repeatableConfig?.addButtonLabel || 'Ajouter' }}
              </button>
            </div>
          </div>
          <div v-else class="space-y-4 pt-4">
            <div v-for="(item, index) in model[section.id]" :key="index" class="p-4 bg-base-200/50 rounded-lg relative border border-base-300">
              <button
                v-if="!props.fieldsDisabled"
                class="btn btn-circle btn-xs btn-ghost absolute top-2 right-2 text-error hover:bg-error/20"
                @click="removeRepeatableItem(section.id, index)"
              >
                ✕
              </button>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <template v-for="field in section.fields" :key="field.id">
                  <div
                    v-if="shouldShow(field, item, index)"
                    :class="[
                      field.type === 'wysiwyg' ||
                      field.type === 'section' ||
                      field.type === 'under-section' ||
                      field.repeatable ||
                      field.fields ||
                      field.newRow
                        ? 'md:col-span-2'
                        : 'md:col-span-1',
                      activeFieldId === field.id ? 'z-[1001] relative' : 'z-auto',
                    ]"
                    class="w-full"
                  >
                    <template v-if="field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields">
                      <div v-if="shouldShow(field, item, index)" class="mt-4 border-l-2 border-base-200 pl-4 py-2 w-full md:col-span-2">
                        <div
                          v-if="field.label || field.name || (field.repeatable && item[field.id]?.length)"
                          class="font-semibold text-base mb-2 flex items-center gap-2"
                        >
                          {{ formatLabel(field.label, field.name, index) }}
                          <span v-if="field.repeatable && item[field.id]" class="badge badge-sm badge-ghost">
                            {{ item[field.id].length }}
                          </span>
                        </div>
                        <DynamicForm
                          v-model="model[section.id][index]"
                          :schema="{
                            ...schema,
                            sections: [
                              {
                                id: field.id,
                                name: formatLabel(field.label, field.name, index),
                                fields: field.fields || [],
                                sections: field.sections || [],
                                repeatable: field.repeatable,
                                repeatableConfig: field.repeatableConfig,
                                condition: field.condition,
                              },
                            ],
                          }"
                          :order-files="orderFiles"
                          :fields-disabled="props.fieldsDisabled"
                          :repeatable-index="index"
                          embed-sections
                          @delete-file="handleDeleteFile"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <TextInput
                        v-if="field.type === 'text' || field.type === 'textarea'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :label="formatLabel(field.label, undefined, index)"
                        :placeholder="field.placeholder"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                      />
                      <WysiwygInput
                        v-else-if="field.type === 'wysiwyg'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :name="formatFieldName(field.name, index)"
                        :label="formatLabel(field.label, undefined, index)"
                        :placeholder="field.placeholder"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                      />
                      <NumberInput
                        v-else-if="field.type === 'number'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :label="formatLabel(field.label, undefined, index)"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :min="field.validation?.min"
                        :step="field.validation?.step ?? (field.type === 'number' && field.name.includes('amount') ? '0.01' : 'any')"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                      />
                      <DateInput
                        v-else-if="field.type === 'date'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :label="formatLabel(field.label, undefined, index)"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                      />
                      <OrderFileInput
                        v-else-if="field.type === 'file'"
                        :name="formatFieldName(field.name, index)"
                        :label="formatLabel(field.label, undefined, index)"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :accept="field.accept"
                        :multiple="field.multiple"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                        :existing-files="getExistingFilesForField(field.name, index)"
                        @change="(files) => (model[section.id][index][formatFieldName(field.name, index)] = files)"
                        @delete-file="handleDeleteFile"
                      />
                      <SelectInput
                        v-else-if="field.type === 'select'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :label="formatLabel(field.label, undefined, index)"
                        :options="getOptions(field, index)"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :multiple="field.multiple"
                        :placeholder="field.placeholder"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                        @open="
                          () => {
                            setSectionActive(section.id, true)
                            setFieldActive(field.id)
                          }
                        "
                        @close="
                          () => {
                            setSectionActive(section.id, false)
                            setFieldActive(null)
                          }
                        "
                      />
                      <SwitchInput
                        v-else-if="field.type === 'switch'"
                        v-model="model[section.id][index][formatFieldName(field.name, index)]"
                        :label="formatLabel(field.label, undefined, index)"
                        :required="field.required"
                        :disabled="isFieldDisabled(field)"
                        :error-message="errors[`${section.id}[${index}].${formatFieldName(field.name, index)}`]"
                      />
                    </template>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="!props.fieldsDisabled" class="flex justify-center py-2">
              <button class="btn btn-outline btn-sm btn-primary gap-2" @click="addRepeatableItem(section)">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {{ section.repeatableConfig?.addButtonLabel || 'Ajouter' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style>
.collapse {
  overflow: visible !important;
  z-index: auto;
}

.collapse-title,
.collapse-content {
  overflow: visible !important;
}

.collapse[style*='z-index: 1000'] {
  z-index: 1000 !important;
}

.collapse:not(.collapse-close) {
  overflow: visible !important;
}

.collapse-content {
  visibility: visible !important;
  display: block !important;
}
</style>

<style scoped>
.z-inherit {
  z-index: inherit;
}
</style>
